import { useOrgConfig } from "../../Common/Hooks/OrgConfigHooks";
import { useDispatch } from "react-redux";
import { useState, useEffect } from 'react';
import { Card, Col, Row } from 'antd';
import { APIClient } from '../../helpers/api_helper';
import Breadcrumb from '../../Common/Breadcrumb';
import IntentDrawer from '../../Common/IntentDrawer';
import RepeatExpiryAlerts from '../../Common/RepeatExpiryAlerts';
import MoneyDisplay from '../../Common/MoneyDisplay';
import utils from '../../Common/utils';
import { setInitialFilterConf } from '../../slices/filters/thunk'
import usecustomStyles from '../../Common/customStyles';
import { AlertCircle, AlertTriangle, Euro, Check } from 'lucide-react';
import CountUp from 'react-countup';
import {styled} from 'styled-components';
import { Link } from 'react-router-dom';


const customStyles = usecustomStyles();

const TextMuted = styled.div`
      color: ${({ theme }) => theme.token.textMute};
`;

const BgInfo = styled.div`
      background-color: ${({ theme }) => theme.token.colorInfoBg};
`;

const BgSuccess = styled.div`
      background-color: ${({ theme }) => theme.token.colorSuccessBg};
`;
const BgWarning = styled.div`
      background-color: ${({ theme }) => theme.token.colorWarningBg};
`;
const BgDanger = styled.div`
      background-color: ${({ theme }) => theme.token.colorDangerBg};
`;


const api = new APIClient();


const Dashboard = () => {
  document.title = "Nadzorna plošča" + process.env.REACT_APP_PAGE_TITLE;

  const dispatch = useDispatch();
  const [dashdata, setDashdata] = useState({});
  const [editedIntentId, setEditedIntentId] = useState(null);
  const [intentDrawerOpen, setIntentDrawerOpen] = useState(false);
  // Bumped after an edit so the warning bars refetch without re-running the
  // dashboard's per-account cash-flow computation.
  const [alertsReloadKey, setAlertsReloadKey] = useState(0);


  // This page renders no filter controls at all, so it must apply none. Filter
  // state is shared app-wide and rides along in the URL hash, so sending it
  // here would let e.g. `unpaid_only` from a tile's own deep link come back
  // round and corrupt the tiles and account balances — with no control on
  // screen to clear it. Each card defines its own fixed filters server-side.
  const loadDashData = () => {
    api.get('/api/dashboard/', {}).then((resp) => {
      setDashdata(resp)
    });
  }

  useEffect(() => {
    dispatch(setInitialFilterConf({
      data: {},
      hidden: ['intent_type', 'intent_checkboxes_group', 'relateds_group', 'search', 'dt_range'],
      disabled: ['search']
    }))
  }, []);

  useEffect(() => {
    loadDashData()
  }, []);


  const successSquareComp = (
    <BgSuccess style={{ padding: '6px', width: '48px', height: '48px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Check style={{ color: customStyles.colorSuccess, }} />
    </BgSuccess>
  )


  const warnSquareComp = (
    <BgDanger style={{ padding: '6px', width: '48px', height: '48px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AlertTriangle style={{ color: customStyles.colorWarning }} />
    </BgDanger>
  )


  const todayUnrealized = dashdata && dashdata.today_unrealized_intents_count;
  const pastUnrealized = dashdata && dashdata.past_unrealized_intents_count;
  const orgConfig = useOrgConfig();
  const notIssued = dashdata && dashdata.not_issued_intents_count;


  return (
    <>
      <Breadcrumb parentTitle="" pageTitle="Nadzorna plošča" />

      <IntentDrawer
        open={intentDrawerOpen}
        onCancel={() => {
          setIntentDrawerOpen(false);
          setEditedIntentId(null);
        }}
        onDone={() => {
          setIntentDrawerOpen(false);
          setEditedIntentId(null);
          loadDashData();
          setAlertsReloadKey((k) => k + 1);
        }}
        onIntentChanged={() => {
          loadDashData();
          setAlertsReloadKey((k) => k + 1);
        }}
        onDelete={() => {
          setIntentDrawerOpen(false);
          setEditedIntentId(null);
          loadDashData();
          setAlertsReloadKey((k) => k + 1);
        }}
        editedIntentId={editedIntentId}
      />

      <RepeatExpiryAlerts
        reloadKey={alertsReloadKey}
        onEdit={(intentId) => {
          setEditedIntentId(intentId);
          setIntentDrawerOpen(true);
        }}
      />

    <Row gutter={[16, 16]}>
      {/* Left column: single Card wrapping the 3 intent blocks */}
      <Col span={12}>
        <Card title="Pregled transakcij" style={{ height: '100%' }}>
          <Row gutter={[16, 16]}>
            {/* To pay TODAY */}
            <Col span={24}>
              <Card size="small" type="inner" bodyStyle={{ padding: '12px' }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ textTransform: "uppercase", fontSize: "13px" }}>
                      Za plačilo <b>DANES</b>
                    </p>
                    {todayUnrealized !== undefined && (
                      <>
                        <h4 style={{ fontSize: "22px" }}>
                          <CountUp start={0} end={todayUnrealized?.count} duration={1} /> transakcij
                          <span style={{ marginLeft: "12px" }}>
                            <MoneyDisplay size="med" type="EXPENSE" val={todayUnrealized?.amount_sum} />
                          </span>
                        </h4>
                        <Link
                          to={`/intents${utils.getURLFromObject({
                            date_range: "today",
                            unpaid_only: 1,
                            override_page_title: 'Transakcije - ZA PLAČILO DANES'
                          })}`}
                        >
                          Prikaži
                        </Link>
                      </>
                    )}
                  </div>
                  {todayUnrealized?.count > 0 ? warnSquareComp : successSquareComp}
                </div>
              </Card>
            </Col>

            {/* PAST unpaid */}
            <Col span={24}>
              <Card size="small" type="inner" bodyStyle={{ padding: '12px' }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ textTransform: "uppercase", fontSize: "13px" }}>
                      <b>PRETEKLE</b> neplačane
                    </p>
                    {pastUnrealized !== undefined && (
                      <>
                        <h4 style={{ fontSize: "22px" }}>
                          <CountUp start={0} end={pastUnrealized?.count} duration={1} /> transakcij
                          <span style={{ marginLeft: "12px" }}>
                            <MoneyDisplay size="med" type="EXPENSE" val={pastUnrealized?.amount_sum} />
                          </span>
                        </h4>
                        <Link
                          to={`/intents${utils.getURLFromObject({
                            date_range: "past_not_today",
                            unpaid_only: 1,
                            override_page_title: 'Transakcije - PRETEKLE NEPLAČANE'
                          })}`}
                        >
                          Prikaži
                        </Link>
                      </>
                    )}
                  </div>
                  {pastUnrealized?.count > 0 ? warnSquareComp : successSquareComp}
                </div>
              </Card>
            </Col>

            {/* NOT issued but should be -- invoice concept, business mode only */}
            {orgConfig.track_issuance && (
            <Col span={24}>
              <Card size="small" type="inner" bodyStyle={{ padding: '12px' }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ textTransform: "uppercase", fontSize: "13px" }}>
                      <b>NEIZDANE</b> (zapadle)
                    </p>
                    {notIssued !== undefined && (
                      <>
                        <h4 style={{ fontSize: "22px" }}>
                          <CountUp start={0} end={notIssued?.count} duration={1} /> transakcij
                          <span style={{ marginLeft: "12px" }}>
                            <MoneyDisplay size="med" type="INCOME" val={notIssued?.amount_sum} />
                          </span>
                        </h4>
                        <Link
                          to={`/intents${utils.getURLFromObject({
                            not_issued_due: 1,
                            override_page_title: 'Transakcije - NEIZDANE (zapadle)'
                          })}`}
                        >
                          Prikaži
                        </Link>
                      </>
                    )}
                  </div>
                  {notIssued?.count > 0 ? warnSquareComp : successSquareComp}
                </div>
              </Card>
            </Col>
            )}

            {/* Upcoming +7D */}
            <Col span={24}>
              <Card size="small" type="inner" bodyStyle={{ padding: '12px' }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ textTransform: "uppercase", fontSize: "13px" }}>
                      Prihajajoče <b>+7D</b>
                    </p>
                    {dashdata?.upcoming_7d_unrealized_intents_count !== undefined && (
                      <>
                        <h4 style={{ fontSize: "22px" }}>
                          <CountUp
                            start={0}
                            end={dashdata.upcoming_7d_unrealized_intents_count?.count}
                            duration={1}
                          />{" "}
                          transakcij
                          <span style={{ marginLeft: "12px" }}>
                            <MoneyDisplay size="med" type="EXPENSE" val={dashdata.upcoming_7d_unrealized_intents_count?.amount_sum} />
                          </span>

                        </h4>
                        <Link
                          to={`/intents${utils.getURLFromObject({
                            date_range: "+7d",
                            unpaid_only: 1,
                            override_page_title: 'Transakcije - PRIHAJAJOČE +7D'
                          })}`}
                        >
                          Prikaži
                        </Link>
                      </>
                    )}
                  </div>
                  <BgInfo
                    style={{
                      padding: "6px",
                      width: "48px",
                      height: "48px",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AlertCircle style={{ color: customStyles.colorInfo }} />
                  </BgInfo>
                </div>
              </Card>
            </Col>
          </Row>
        </Card>
      </Col>

      {/* Right column: single Card wrapping all accounts */}
      <Col span={12}>
        <Card title="Načrtovano stanje računov za danes" style={{ height: '100%' }}>
          <Row gutter={[16, 16]}>
            {dashdata?.cashflows_by_acc &&
              Object.entries(dashdata.cashflows_by_acc).map(([key, data]) => (
                <Col span={24} key={key}>
                  <Card size="small" type="inner" bodyStyle={{ padding: '12px' }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        height: "107px",
                      }}
                    >
                      <div>
                        <p style={{ textTransform: "uppercase", fontSize: "13px" }}>
                          {data.account ? data.account.title : "Vsi računi"}
                        </p>
                        <h4 style={{ fontSize: "22px" }}>
                          <MoneyDisplay
                            size="med"
                            type={data.cashflow_balance_today < 0 ? "EXPENSE" : "INCOME"}
                            val={data.cashflow_balance_today}
                          />
                        </h4>
                      </div>
                      <BgSuccess
                        style={{
                          padding: "6px",
                          width: "48px",
                          height: "48px",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Euro style={{ color: customStyles.colorInfo }} />
                      </BgSuccess>
                    </div>
                  </Card>
                </Col>
              ))}
          </Row>
        </Card>
      </Col>
    </Row>
    </>
  )
}

export default Dashboard;
